import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import {
  Search,
  Trash2,
  Eye,
  Smartphone,
  FileText,
  Globe,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import { debounce } from "lodash";
import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import TemplateStatusChip from "../../widgets/TemplateStatusChip";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

// Helper for status styling
const getStatusProps = (status) => {
  const s = (status || "pending").toLowerCase();
  switch (s) {
    case "approved":
      return { color: "success", icon: <CheckCircle2 size={16} /> };
    case "rejected":
      return { color: "error", icon: <XCircle size={16} /> };
    case "pending":
      return { color: "warning", icon: <Clock size={16} /> };
    default:
      return { color: "default", icon: <Clock size={16} /> };
  }
};

const RcsTemplates = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
    }, 500),
    []
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/rcs/get-templates?sq=${searchQuery}`
  );
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const templates = data || [];

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      await fetch(
        `${API_ENDPOINT}/rcs/delete-template/${templateToDelete._id}`,
        {
          method: "DELETE",
          ...getHeader(),
        }
      );
      refetch();
      if (selectedTemplate?._id === templateToDelete._id) {
        setSelectedTemplate(null);
      }
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error("Error deleting template:", err);
      alert("Failed to delete template. Please try again.");
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, minHeight: "100vh", bgcolor: "grey.50" }}>
      <BackBtn />

      <Box sx={{ mb: 3, mt: 1 }}>
        <PageTitle mb={1}>RCS Templates</PageTitle>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search templates by name..."
          value={searchInput}
          onChange={handleSearchChange}
          sx={{
            maxWidth: 400,
            bgcolor: "white",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} color="#757575" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TemplatesList
        isMobile={isMobile}
        templates={templates}
        selectedTemplate={selectedTemplate}
        isLoading={isLoading}
        isError={isError}
        error={error}
        searchQuery={searchQuery}
        setSelectedTemplate={setSelectedTemplate}
        setTemplateToDelete={setTemplateToDelete}
        setDeleteDialogOpen={setDeleteDialogOpen}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AlertCircle size={24} color="#d32f2f" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template{" "}
            <strong>"{templateToDelete?.templateName}"</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const TemplatesList = ({
  isMobile,
  templates,
  selectedTemplate,
  isLoading,
  error,
  isError,
  searchQuery,
  setSelectedTemplate,
  setTemplateToDelete,
  setDeleteDialogOpen,
}) => {
  if (isLoading) return <TemplateListSkeleton />;

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
        Error loading templates: {error?.message || "Unknown error"}
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Paper
          elevation={0}
          sx={{
            height: isMobile ? "400px" : "80vh",
            maxHeight: isMobile ? "400px" : "80vh",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Box sx={{ p: 2, bgcolor: "primary.main", color: "white" }}>
            <Typography variant="h6" component="h2" fontWeight="600">
              Templates ({templates.length})
            </Typography>
            {searchQuery && (
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Results for "{searchQuery}"
              </Typography>
            )}
          </Box>

          {templates.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileText
                size={48}
                color="#bdbdbd"
                style={{ marginBottom: 16 }}
              />
              <Typography variant="body1" color="text.secondary">
                {searchQuery
                  ? "No templates found matching your search."
                  : "No templates available."}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, flex: 1, overflow: "auto" }}>
              {templates.map((template, index) => {
                const statusProps = getStatusProps(template.templateStatus);

                return (
                  <React.Fragment key={template._id}>
                    <ListItem
                      button
                      selected={selectedTemplate?._id === template._id}
                      onClick={() => setSelectedTemplate(template)}
                      sx={{
                        p: 2,
                        transition: "all 0.2s",
                        "&:hover": { bgcolor: "action.hover" },
                        "&.Mui-selected": {
                          bgcolor: "primary.50",
                          borderLeft: "4px solid",
                          borderColor: "primary.main",
                          "&:hover": { bgcolor: "primary.50" },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 48 }}>
                        <Avatar
                          sx={{
                            bgcolor: "primary.light",
                            color: "primary.dark",
                            width: 40,
                            height: 40,
                          }}
                        >
                          <FileText size={20} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            noWrap
                          >
                            {template?.templateName}
                          </Typography>
                        }
                        secondary={
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={(
                                template.communicationType || "N/A"
                              ).toUpperCase()}
                              size="small"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                            <Chip
                              icon={statusProps.icon}
                              label={template.templateStatus || "pending"}
                              size="small"
                              color={statusProps.color}
                              variant="outlined"
                              sx={{
                                fontSize: "0.7rem",
                                height: 20,
                                textTransform: "capitalize",
                                pl: 0.5,
                              }}
                            />
                          </Box>
                        }
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Tooltip title="Preview">
                          <IconButton edge="end" size="small" color="primary">
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTemplateToDelete(template);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    {index < templates.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        {selectedTemplate ? (
          <RcsTemplatePreview template={selectedTemplate} />
        ) : (
          <Paper
            elevation={0}
            sx={{
              height: isMobile ? 400 : "80vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <Smartphone
              size={64}
              color="#e0e0e0"
              style={{ marginBottom: 16 }}
            />
            <Typography variant="h6" color="text.secondary">
              {templates.length === 0
                ? "No templates to preview"
                : "Select a template to view details"}
            </Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export const RcsTemplatePreview = ({ template }) => {
  const theme = useTheme();

  const getTemplateContent = () => {
    if (!template?.content) return null;

    if (template?.templateType === "standard") {
      const c = template.content;
      if (
        c.documentUrl ||
        template.subtemplateType === "text_message_with_media"
      ) {
        const fileName = c.documentFileName || "attachment";
        const isPdf = fileName.toLowerCase().endsWith(".pdf");
        return {
          type: "textWithMedia",
          documentUrl: c.documentUrl,
          documentFileName: fileName,
          messageText: c.messageText || "",
          messageOrder: c.messageOrder || "text_message_at_top",
          suggestions: c.suggestions || [],
          isPdf,
        };
      }
      if (c.messageText) {
        return {
          type: "plain",
          text: c.messageText,
          suggestions: c.suggestions || [],
        };
      }
    }

    if (template.templateType === "richCard") {
      return {
        type: "richCard",
        imageUrl: template.content.imageUrl,
        orientation: template.content.orientation,
        alignment: template.content.alignment,
        height: template.content.height,
        fileType: template.content.fileType,
        messageText: template.content.messageText,
        messageDescription: template.content.messageDescription,
        suggestions: template.content.suggestions || [],
      };
    }

    if (template.content.plainText) {
      return {
        type: "plain",
        text: template.content.plainText,
        suggestions: template.content.suggestions || [],
      };
    }

    if (template.content.richCardDetails) {
      const rc = template.content.richCardDetails;
      if (rc.standalone) {
        return {
          type: "standalone",
          card: rc.standalone.content,
          orientation: rc.standalone.cardOrientation,
          suggestions: rc.standalone.content?.suggestions || [],
        };
      }
      if (rc.carousel) {
        return {
          type: "carousel",
          cards: rc.carousel.contents || [],
          suggestions: rc.carousel.suggestions || [],
        };
      }
    }
    return null;
  };

  const content = getTemplateContent();
  const isRejected =
    (template.templateStatus || "").toLowerCase() === "rejected";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      {/* Header Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" fontWeight="700" gutterBottom>
          {template.templateName}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
          <Chip
            icon={<Globe size={16} />}
            label={`Type: ${template.templateType}`}
            variant="outlined"
            size="small"
            sx={{ pl: 0.5 }}
          />
          <Chip
            icon={<Calendar size={16} />}
            label={new Date(template.createdAt).toLocaleDateString()}
            variant="outlined"
            size="small"
            sx={{ pl: 0.5 }}
          />
          <TemplateStatusChip status={template.templateStatus} />
        </Box>
      </Box>

      {/* Rejection Reason Alert */}
      {isRejected && (
        <Alert
          severity="error"
          icon={<AlertCircle size={24} />}
          sx={{ mb: 4, borderRadius: 2, alignItems: "center" }}
        >
          <Typography variant="subtitle2" fontWeight="600">
            Template Rejected
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {template.comment ||
              template.rejectionReason ||
              "No specific reason provided by the reviewer."}
          </Typography>
        </Alert>
      )}

      {/* Simulated Phone UI Preview */}
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: 340,
          border: "12px solid #2c2c2c",
          borderRadius: "36px",
          bgcolor: "#f5f6f8",
          position: "relative",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Fake Phone Notch */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 120,
            height: 24,
            bgcolor: "#2c2c2c",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            zIndex: 10,
          }}
        />

        <Box
          sx={{
            p: 2,
            pt: 4,
            bgcolor: "white",
            color: "text.primary",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" textAlign="center">
            {template.templateName}
          </Typography>
        </Box>

        {/* --- SCROLLABLE PHONE CONTENT AREA --- */}
        <Box
          sx={{
            p: 2,
            height: 480,
            overflowY: "auto",
            // Custom thin scrollbar so it looks native to mobile
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {content ? (
            <Card
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 3,
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {/* Text + Media */}
              {content.type === "textWithMedia" && (
                <Box>
                  {content.messageText &&
                    content.messageOrder === "text_message_at_top" && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-line", mb: 2, lineHeight: 1.6 }}
                      >
                        {content.messageText}
                      </Typography>
                    )}

                  {content.documentUrl && (
                    <Box sx={{ mb: 2 }}>
                      {content.isPdf ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            borderRadius: 2,
                            textAlign: "center",
                            bgcolor: "grey.50",
                          }}
                        >
                          <FileText
                            size={48}
                            color="#f44336"
                            style={{ marginBottom: 12 }}
                          />
                          <Typography variant="subtitle2" fontWeight="bold">
                            {content.documentFileName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            PDF Document
                          </Typography>
                        </Paper>
                      ) : (
                        <Box
                          component="img"
                          src={content.documentUrl}
                          alt={content.documentFileName}
                          sx={{
                            width: "100%",
                            maxHeight: 200,
                            objectFit: "cover", // better than contain for mobile cards if we want a full bleed, or keep contain based on preference
                            borderRadius: 2,
                            bgcolor: "#000",
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {content.messageText &&
                    content.messageOrder !== "text_message_at_top" && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-line", mt: 2, lineHeight: 1.6 }}
                      >
                        {content.messageText}
                      </Typography>
                    )}
                </Box>
              )}

              {/* Plain Text */}
              {content.type === "plain" && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.5 }}
                >
                  {content.text}
                </Typography>
              )}

              {/* Rich Card */}
              {content.type === "richCard" && (
                <Box
                  sx={{
                    display:
                      content.orientation === "HORIZONTAL" ? "flex" : "block",
                    alignItems: "center",
                    ...(content.orientation === "HORIZONTAL" && {
                      flexDirection:
                        content.alignment === "LEFT" ? "row" : "row-reverse",
                      gap: 2,
                    }),
                  }}
                >
                  {content.imageUrl && content.fileType === "IMAGE" && (
                    <Box
                      component="img"
                      src={content.imageUrl}
                      alt={content.messageText}
                      sx={{
                        width:
                          content.orientation === "HORIZONTAL" ? "40%" : "100%",
                        height: content.height || 120,
                        objectFit: "cover",
                        borderRadius: 2,
                        mb: content.orientation !== "HORIZONTAL" ? 1.5 : 0,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="600"
                      gutterBottom
                    >
                      {content.messageText}
                    </Typography>
                    {content.messageDescription && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        {content.messageDescription}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Suggestions Chips */}
              {content.suggestions?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {content.suggestions.map((suggestion, idx) => (
                      <Chip
                        key={idx}
                        label={
                          suggestion.action?.text ||
                          suggestion.action?.plainText ||
                          suggestion.reply?.plainText
                        }
                        variant="outlined"
                        size="medium"
                        color="primary"
                        sx={{ borderRadius: 4, fontWeight: "500" }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Card>
          ) : (
            <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
              <Typography variant="body2">
                No preview content available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Tech Details Section */}
      <Box
        sx={{
          mt: 5,
          p: 3,
          bgcolor: "grey.50",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight="600"
          gutterBottom
          sx={{ mb: 2 }}
        >
          Metadata
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Template ID
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {template.templateId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Customer ID
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {template.customerId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              User ID
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {template.userId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Updated On
            </Typography>
            <Typography variant="body2" fontWeight="500">
              {new Date(template.updatedAt).toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const TemplateListSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton
        variant="rectangular"
        width={250}
        height={32}
        sx={{ mb: 2, borderRadius: 1 }}
      />
      <Skeleton
        variant="rectangular"
        width={400}
        height={40}
        sx={{ mb: 4, borderRadius: 2 }}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #eee",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Skeleton variant="rectangular" height={64} />
            {[...Array(4)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #eee",
                }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ mr: 2 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Skeleton
            variant="rectangular"
            height={500}
            sx={{ borderRadius: 3 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RcsTemplates;
